import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isSet } from '@app/common/pl-util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateTime } from 'luxon';
import {
  PlDesignService,
  PlBaseGridComponent,
  PlGridView,
  PlGridAction,
  PlGridColumn,
  PlRowAction,
} from 'pl-core-services';
import { filter } from 'rxjs';

export interface PlTableCountDisplay {
  prefix: string;
  single: string;
  multiple: string;
  suffix?: string;
}

@UntilDestroy()
@Component({
  selector: 'pl-table',
  templateUrl: './pl-table.component.html',
  styleUrls: ['./pl-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class PlTableComponent
  extends PlBaseGridComponent
  implements OnInit, OnChanges, AfterViewChecked
{
  @Input() hasBorder = true;

  @Input() showSearch = true;

  @Input() override gridViews: PlGridView[];

  @Input() override columns: PlGridColumn[] = [];

  @Input() override rowActions: PlRowAction[];

  @Input() override view: PlGridView;

  @Input() override selectable: boolean;

  @Input() expandable: boolean;

  @Input() expandableTemplate: TemplateRef<any>;

  @Input() tableTitle: string;

  @Input() count: number;

  @Input() filteredCount: number;

  @Input() loading: boolean;

  @Input() data: any[];

  @Input() pagination: boolean;

  @Input() fetchDate: DateTime;

  @Input() defaultSort: string;

  @Input() uniqueColumn: string;

  @Input() countDisplay: PlTableCountDisplay = {
    prefix: 'Showing',
    single: 'Record',
    multiple: 'Records',
  };

  @Input() sortCompare: (a: any, b: any, columnId: string) => number;

  @Output() actionTriggered: EventEmitter<PlGridAction<any, any>> =
    new EventEmitter<PlGridAction<any, any>>();

  @Output() viewChange: EventEmitter<PlGridView> =
    new EventEmitter<PlGridView>();

  @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

  @Output() selectionChange: EventEmitter<any[]> = new EventEmitter();

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  @ViewChild('customEmptyTemplate') customEmptyTemplate: any;

  @ViewChild('cardBodyTemplate') cardBodyTemplate: any;

  @ViewChild(MatSort) sort: MatSort;

  selection = new SelectionModel<any>(true, []);

  dataSource: MatTableDataSource<any>;

  searchTerm = new UntypedFormControl();

  expandedElementUuid: any;

  constructor(
    public plDesign: PlDesignService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { data } = changes;
    if (data && this.dataSource) {
      this.dataSource.data = this.data;
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.expandable && !this.uniqueColumn) {
      throw new Error('`expandable` requires `uniqueColumn`');
    }
    this.selection.changed
      .pipe(untilDestroyed(this))
      .subscribe(() => this.selectionChange.emit(this.selection.selected));
    setTimeout(
      () =>
        this.pageChange.emit({
          pageSize: this.plDesign.settings.paginator.size,
          pageIndex: 1,
          length: this.count,
        }),
      0
    );
  }

  ngAfterViewChecked() {
    //
    if (!this.dataSource && this.data && this.data.length > 0) {
      if (this.defaultSort) {
        this.sort.sort({
          id: this.defaultSort,
          start: 'desc',
          disableClear: false,
        });
      }
      this.sort.sortChange
        .pipe(
          filter(() => !!this.expandedElementUuid),
          untilDestroyed(this)
        )
        .subscribe(() => {
          const element = document.getElementById(
            `expanded_detail_${this.expandedElementUuid}`
          );
          if (!element) {
            return;
          }
          element.scrollIntoView({ behavior: 'smooth' });
        });
      this.dataSource = new MatTableDataSource(this.data);
      this.dataSource.sortData = this.sortData;
      this.dataSource.sort = this.sort;
      this.cdr.detectChanges();
    }
  }

  get rowCount(): number {
    if (this.searchTerm?.value) {
      return this.dataSource?.filteredData.length;
    }

    if (isSet(this.filteredCount)) {
      return this.filteredCount;
    }

    return this.dataSource?.data?.length || 0;
  }

  resetSearch() {
    this.searchTerm.setValue('');
    this.applyFilter();
  }

  applyFilter() {
    this.fetchDate = null;
    const filterValue = this.searchTerm.value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  sortData: (data: any[], sort: MatSort) => any[] = (
    data: any[],
    sort: MatSort
  ): any[] => {
    const columnActive = sort.active;
    const { direction } = sort;
    if (!columnActive || direction === '') {
      return data;
    }
    this.fetchDate = null;

    return data.sort((a, b) => {
      const valueA = this.dataSource.sortingDataAccessor(a, columnActive);
      const valueB = this.dataSource.sortingDataAccessor(b, columnActive);

      let comparatorResult = 0;

      comparatorResult = this.sortCompare
        ? this.sortCompare(valueA, valueB, columnActive)
        : this.defaultCompare(valueA, valueB);
      return comparatorResult * (direction === 'asc' ? 1 : -1);
    });
  };

  // This is the original angular material implementation
  defaultCompare(a: string | number, b: string | number): number {
    let valueA = a;
    let valueB = b;
    const valueAType = typeof valueA;
    const valueBType = typeof valueB;

    if (valueAType !== valueBType) {
      if (valueAType === 'number') {
        valueA += '';
      }
      if (valueBType === 'number') {
        valueB += '';
      }
    }

    let comparatorResult = 0;
    if (valueA != null && valueB != null) {
      if (valueA > valueB) {
        comparatorResult = 1;
      } else if (valueA < valueB) {
        comparatorResult = -1;
      }
    } else if (valueA != null) {
      comparatorResult = 1;
    } else if (valueB != null) {
      comparatorResult = -1;
    }
    return comparatorResult;
  }

  onPage(event: PageEvent) {
    this.plDesign.setTablePageSize(event.pageSize);
    this.pageChange.emit({
      ...event,
      pageIndex: event.pageIndex + 1,
    });
  }

  setView(view: PlGridView) {
    if (this.view?.key !== view.key) {
      this.viewChange.emit(view);
      this.paginator.firstPage();
      this.view = view;
    }
  }

  isNewRow(row: { created?: DateTime }) {
    return this.fetchDate && row.created && row.created > this.fetchDate;
  }

  formatTime(date: DateTime): string {
    return date.toLocaleString(DateTime.TIME_SIMPLE);
  }

  // #region Selection
  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach((row) => this.selection.select(row));
    }
  }
  // #endregion
}
